import { useEffect, useRef, ReactNode } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  loadAllWorkspaces,
  loadAllNotes,
  selectShouldFetchWorkspaces,
} from "../store/slices/workspaceSlice";
import { loadLinks, selectShouldFetchLinks } from "../store/slices/linkSlice";
import { selectShouldFetchThreads } from "../store/slices/threadSlice";
import { useAuth } from "../hooks/useAuth";
import { selectIsReadOnly } from "../store/slices/cacheSlice";

/** While the API keeps failing with the browser online, how often to try again. */
export const RECONNECT_PROBE_MS = 30_000;

interface ReduxInitializerProps {
  children: ReactNode;
}

export function ReduxInitializer({ children }: ReduxInitializerProps) {
  const dispatch = useAppDispatch();
  // Wait for the start-up session check, not just stored tokens: it may be
  // refreshing them, or about to find them dead.
  const { isReady: authReady, user, userEmail } = useAuth();
  // Who it is: moves when the signed-in user changes.
  const userKey = user?.sub ?? userEmail;

  // Cache-first: Check if we need to fetch data
  const shouldFetchWorkspaces = useAppSelector(selectShouldFetchWorkspaces);
  const shouldFetchLinks = useAppSelector(selectShouldFetchLinks);
  const shouldFetchThreads = useAppSelector(selectShouldFetchThreads);

  useEffect(() => {
    if (authReady) {
      const loadData = async () => {
        // Cache-first: only fetch if cache is expired or empty. Workspaces
        // carry their groups inline, so groupSlice populates automatically via
        // its extraReducer on loadAllWorkspaces.fulfilled — no separate
        // /groups call needed.
        if (shouldFetchWorkspaces) {
          dispatch(loadAllWorkspaces(undefined));
        }

        if (shouldFetchThreads) {
          dispatch(loadAllNotes());
        }

        if (shouldFetchLinks) {
          dispatch(loadLinks());
        }
      };
      loadData();
    }
    // userKey moves when the signed-in user changes and the store's data
    // slices are reset: load the new user's data even if the flags above
    // read the same as before.
  }, [
    dispatch,
    authReady,
    userKey,
    shouldFetchWorkspaces,
    shouldFetchLinks,
    shouldFetchThreads,
  ]);

  // Back online: refetch everything the page shows, so the saved copy is
  // replaced and cached mode ends on the first success. While the API keeps
  // failing with the browser online (cached mode), try again every
  // RECONNECT_PROBE_MS, and when the tab comes back into view.
  const browserOffline = useAppSelector((state) => state.cache.browserOffline);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const wasOffline = useRef(browserOffline);
  useEffect(() => {
    const cameBack = wasOffline.current && !browserOffline;
    wasOffline.current = browserOffline;
    if (!authReady || browserOffline) return;
    const refetch = () => {
      dispatch(loadAllWorkspaces({ forceRefresh: true }));
      dispatch(loadAllNotes());
      dispatch(loadLinks());
    };
    if (cameBack) refetch();
    if (!isReadOnly) return;
    const timer = setInterval(refetch, RECONNECT_PROBE_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [dispatch, authReady, browserOffline, isReadOnly]);

  return children;
}
